// PATH: src/context/AuthContext.tsx
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";

import { useAuthApi } from "../api/auth/auth";
import type { CoreUser, PatchMeInput } from "../api/auth/types";
import { createAuthSync, type AuthEvent } from "../lib/authSync";

type AuthStatus = "unknown" | "authenticated" | "anonymous";

/** 💾 Structure standardisée des métadonnées utilisateur */
export type UserMetadata = {
    language?: string;
    onBoardingTilmidPlus?: boolean;
    programId?: string;
    stageId?: string;
    gradeId?: string;
    [k: string]: unknown;
};

/** Version app de l'utilisateur, avec metadata typée */
export interface AppUser extends CoreUser {
    metadata?: UserMetadata | null;
}

export interface AuthProviderProps {
    onStatusChange?: (status: AuthStatus) => void;
    children: ReactNode;
}

interface AuthContextValue<TUser extends AppUser = AppUser> {
    status: AuthStatus;
    isLoading: boolean;
    user: TUser | null;

    login: (username: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    refreshMe: () => Promise<TUser | null>;

    updateMe: (input: PatchMeInput) => Promise<TUser | null>;
    updateMetadataKey: (key: keyof UserMetadata, value: unknown) => Promise<TUser | null>;
    updateMetadata: (patch: Partial<UserMetadata>) => Promise<TUser | null>;

    setLanguage: (language: string) => Promise<TUser | null>;
    completeOnboardingTilmidPlus: () => Promise<TUser | null>;
    setSchoolPath: (p: { programId?: string; stageId?: string; gradeId?: string }) => Promise<TUser | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth<TUser extends AppUser = AppUser>(): AuthContextValue<TUser> {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
    return ctx as AuthContextValue<TUser>;
}

export function AuthProvider({ onStatusChange, children }: AuthProviderProps) {
    const { me, loginBasic, logout, patchMe, updateMetadataKey, isAuthenticated } = useAuthApi();
    const [status, setStatus] = useState<AuthStatus>("unknown");
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<AppUser | null>(null);
    const syncRef = useRef(createAuthSync());

    // --- Refs pour éviter closures périmées ---
    const onStatusChangeRef = useRef(onStatusChange);
    useEffect(() => {
        onStatusChangeRef.current = onStatusChange;
    }, [onStatusChange]);

    const applyStatus = useCallback((s: AuthStatus) => {
        setStatus(s);
        onStatusChangeRef.current?.(s);
    }, []);

    const refreshMe = useCallback(async (): Promise<AppUser | null> => {
        try {
            setIsLoading(true);
            const data = await me<AppUser>();
            setUser(data);
            applyStatus("authenticated");
            return data;
        } catch {
            setUser(null);
            applyStatus("anonymous");
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [me, applyStatus]);

    const refreshMeRef = useRef(refreshMe);
    useEffect(() => {
        refreshMeRef.current = refreshMe;
    }, [refreshMe]);

    const applyStatusRef = useRef(applyStatus);
    useEffect(() => {
        applyStatusRef.current = applyStatus;
    }, [applyStatus]);

    // --- Auth actions ---
    const login = useCallback(
        async (username: string, password: string): Promise<boolean> => {
            setIsLoading(true);
            try {
                const ok = await loginBasic(username, password);
                if (!ok) {
                    setUser(null);
                    applyStatus("anonymous");
                    return false;
                }
                await refreshMe();
                syncRef.current.publish({ type: "login" });
                return true;
            } finally {
                setIsLoading(false);
            }
        },
        [loginBasic, refreshMe, applyStatus]
    );

    const logoutFn = useCallback(async () => {
        setIsLoading(true);
        try {
            await logout();
            setUser(null);
            applyStatus("anonymous");
            syncRef.current.publish({ type: "logout" });
        } finally {
            setIsLoading(false);
        }
    }, [logout, applyStatus]);

    const updateMe = useCallback(
        async (input: PatchMeInput): Promise<AppUser | null> => {
            setIsLoading(true);
            try {
                await patchMe(input);
                const meData = await refreshMe();
                syncRef.current.publish({ type: "user-updated" });
                return meData;
            } finally {
                setIsLoading(false);
            }
        },
        [patchMe, refreshMe]
    );

    const updateMetadataKeyFn = useCallback(
        async (key: keyof UserMetadata, value: unknown): Promise<AppUser | null> => {
            setIsLoading(true);
            try {
                await updateMetadataKey(String(key), value);
                const meData = await refreshMe();
                syncRef.current.publish({ type: "user-updated" });
                return meData;
            } finally {
                setIsLoading(false);
            }
        },
        [updateMetadataKey, refreshMe]
    );

    const updateMetadata = useCallback(
        async (patch: Partial<UserMetadata>): Promise<AppUser | null> => {
            return updateMe({ metadata: patch });
        },
        [updateMe]
    );

    // -------- Helpers orientés produit --------
    const setLanguage = useCallback(
        async (language: string) => updateMetadata({ language }),
        [updateMetadata]
    );

    const completeOnboardingTilmidPlus = useCallback(
        async () => updateMetadataKeyFn("onBoardingTilmidPlus", true),
        [updateMetadataKeyFn]
    );

    const setSchoolPath = useCallback(
        async (p: { programId?: string; stageId?: string; gradeId?: string }) =>
            updateMetadata({
                ...(p.programId ? { programId: p.programId } : null),
                ...(p.stageId ? { stageId: p.stageId } : null),
                ...(p.gradeId ? { gradeId: p.gradeId } : null),
            }),
        [updateMetadata]
    );

    // --- bootstrap: vérifie si utilisateur déjà connecté ---
    useEffect(() => {
        // Skip auth check on specific public pages to avoid 401-triggered redirects
        // notably email confirmation and password reset flows which are anonymous by design.
        const publicAuthPaths = new Set([
            '/callback/email-confirm',
            '/reset-password',
            // Lien magique d'inscription : le visiteur est anonyme par construction,
            // et la session est ouverte par la page elle-même une fois le mot de
            // passe défini.
            '/set-password',
        ]);
        if (publicAuthPaths.has(window.location.pathname)) {
            setIsLoading(false);
            applyStatusRef.current("anonymous"); // prevent redirects on these pages
            return;
        }
        
        let cancelled = false;
        (async () => {
            try {
                const authed = await isAuthenticated();
                if (cancelled) return;

                if (authed) {
                    const u = await me<AppUser>();
                    if (cancelled) return;
                    setUser(u);
                    applyStatusRef.current("authenticated");
                } else {
                    setUser(null);
                    applyStatusRef.current("anonymous");
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- synchronisation multi-onglets ---
    useEffect(() => {
        const sync = syncRef.current;
        const unsubscribe = sync.subscribe(async (evt: AuthEvent) => {
            if (evt.type === "login" || evt.type === "user-updated") {
                await refreshMeRef.current();
            } else if (evt.type === "logout") {
                setUser(null);
                applyStatusRef.current("anonymous");
            }
        });

        return () => {
            unsubscribe();
            sync.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({
            status,
            isLoading,
            user,
            login,
            logout: logoutFn,
            refreshMe,
            updateMe,
            updateMetadataKey: updateMetadataKeyFn,
            updateMetadata,
            setLanguage,
            completeOnboardingTilmidPlus,
            setSchoolPath,
        }),
        [
            status,
            isLoading,
            user,
            login,
            logoutFn,
            refreshMe,
            updateMe,
            updateMetadataKeyFn,
            updateMetadata,
            setLanguage,
            completeOnboardingTilmidPlus,
            setSchoolPath,
        ]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
